import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, Camera, MessageCircle, ShieldCheck, ChevronRight, Star, BadgeCheck, Users, UtensilsCrossed, Ticket, BedDouble, Music2 } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../components/VIP";
import { sanitizeHtml } from "../lib/sanitize";

const fmtD = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function SampleCard({ r }) {
  const plural = { venue: "venues", artist: "artists", event: "events", attraction: "attractions", activity: "activities" }[r.entity_type] || "venues";
  return (
    <Link to={`/${plural}/${r.entity_slug}`} data-testid={`insider-sample-${r.id}`}
      className="group block rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-transparent p-6 hover:border-amber-400/60 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider bg-amber-400 text-black px-2 py-1 rounded-full">
          <Crown size={11} /> Insider Review
        </span>
        <span className="inline-flex gap-0.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={12} className={n <= Math.round(r.rating || 0) ? "text-amber-400 fill-amber-400" : "text-zinc-600"} />
          ))}
        </span>
      </div>
      <h3 className="font-display font-bold text-lg leading-tight group-hover:text-amber-200">{r.entity_name}</h3>
      {r.title && <p className="text-sm text-zinc-300 mt-1 font-medium">{r.title}</p>}
      <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{r.body}</p>
      <p className="text-xs text-zinc-500 mt-3">The Gulf Coast VIP Team · {fmtD(r.created_at)}</p>
    </Link>
  );
}

function Step({ n, Icon, title, body }) {
  return (
    <div className="flex gap-4" data-testid={`insider-step-${n}`}>
      <div className="shrink-0 w-11 h-11 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">Step {n}</p>
        <p className="font-display font-bold">{title}</p>
        <p className="text-sm text-zinc-400 leading-relaxed mt-1">{body}</p>
      </div>
    </div>
  );
}

export default function InsiderReview() {
  useSeo("What's a VIP Insider Review? | Gulf Coast VIP",
    "A free editorial visit from the Gulf Coast VIP team — identified with credentials. You cover the experience, never a fee. See samples and invite our team.");
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    api.get("/reviews/insider-samples?limit=6").then((r) => setSamples(r.data || [])).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="insider-review-landing">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-black to-black" />
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(ellipse at top right, rgba(251,191,36,0.25), transparent 55%)" }} />
        <div className="relative px-6 md:px-12 lg:px-20 py-20 md:py-24 max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <p className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest bg-amber-400 text-black px-3 py-1.5 rounded-full">
                <Crown size={13} /> The VIP Insider Review
              </p>
              <p className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest border border-emerald-400/50 text-emerald-300 px-3 py-1.5 rounded-full" data-testid="insider-free-pill">
                <BadgeCheck size={13} /> 100% Free
              </p>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
              We visit. <span className="text-amber-300">We experience.</span><br />We reveal what makes it VIP.
            </h1>
            <p className="text-lg text-zinc-300 mt-6 max-w-2xl leading-relaxed">
              A VIP Insider Review is a <strong className="text-white">free</strong>, in-person visit from the Gulf Coast VIP editorial team — arriving <strong className="text-white">identified, with official credentials</strong>, never as mystery shoppers. Your listing earns a permanent, badge-backed feature that stays pinned to your page.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/dashboard" className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2" data-testid="insider-invite-cta">
                <Crown size={16} /> Invite our team
              </Link>
              <a href="#how-it-works" className="border border-amber-400/40 hover:border-amber-400 text-amber-200 font-medium px-6 py-3 rounded-full">
                How it works
              </a>
            </div>
          </div>
          <div className="relative hidden lg:block" data-testid="insider-hero-photo">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-amber-400/30 to-transparent blur-xl" aria-hidden="true" />
            <img src="/insider/hero-team.jpg" alt="The Gulf Coast VIP editorial team reviewing a beachfront venue"
              className="relative rounded-3xl border border-amber-400/30 shadow-2xl rotate-1 w-full object-cover" loading="eager" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-2 rounded-full text-xs font-bold text-amber-200 border border-amber-400/30">
              <ShieldCheck size={14} /> Identified team · official credentials
            </div>
          </div>
        </div>
      </section>

      {/* Value grid */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-6xl mx-auto">
        <p className="overline mb-3">What you get</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-10">A badge, a spotlight, a story.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: Crown, title: "The Insider Badge", body: "A gold Insider Review card that stays pinned above every guest review — a permanent trust signal for anyone who lands on your page." },
            { Icon: Camera, title: "Editorial Photography", body: "Hero shots from our visit that we can feature across your listing and the homepage." },
            { Icon: Sparkles, title: "Full Rich Storytelling", body: "Formatted headlines, images, embeds — the works. Not a Yelp-style paragraph but a full editorial write-up." },
            { Icon: ShieldCheck, title: "Verified by Gulf Coast VIP", body: "Every Insider Review is written by our team, signed by the editorial desk, and dated so guests know it's the real deal." },
            { Icon: MessageCircle, title: "Owner Reply Ready", body: "Reply publicly right under the review to thank the team, share updates, or invite guests to try the same experience." },
            { Icon: Star, title: "Award Eligibility", body: "Insider-reviewed listings are the first ones considered for our seasonal VIP Spotlight Awards nominations." },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid={`insider-value-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <c.Icon size={22} className="text-amber-300 mb-3" />
              <h3 className="font-display font-bold text-lg">{c.title}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Coverage — you provide the experience, never a fee */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-6xl mx-auto border-t border-white/5" data-testid="insider-coverage">
        <p className="overline mb-3">How coverage works</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-4">You cover the experience. <span className="text-amber-300">Never a fee.</span></h2>
        <p className="text-zinc-300 max-w-3xl leading-relaxed mb-10">
          Insider Reviews are free — no invoice, ever. To write honestly about your experience, our team needs to live it, so the host covers the experience itself <strong className="text-white">in-kind</strong>. Tell us exactly what you'd like reviewed and we'll allocate the right resources: team size, photographer or videographer, and an overnight stay when the experience calls for it.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="group relative rounded-3xl overflow-hidden border border-white/10" data-testid="insider-coverage-food">
            <img src="/insider/food-tasting.jpg" alt="Food tasting Insider Review" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <p className="flex items-center gap-2 text-amber-300 text-xs font-extrabold uppercase tracking-widest mb-1.5"><UtensilsCrossed size={14} /> Example</p>
              <h3 className="font-display font-bold text-xl">Food tasting review</h3>
              <p className="text-sm text-zinc-300 mt-1">The venue covers the tasting — our writer and photographer cover the story.</p>
            </div>
          </div>
          <div className="group relative rounded-3xl overflow-hidden border border-white/10" data-testid="insider-coverage-attraction">
            <img src="/insider/attraction-visit.jpg" alt="Attraction Insider Review" className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <p className="flex items-center gap-2 text-amber-300 text-xs font-extrabold uppercase tracking-widest mb-1.5"><Ticket size={14} /> Example</p>
              <h3 className="font-display font-bold text-xl">Attraction review</h3>
              <p className="text-sm text-zinc-300 mt-1">The business covers the tickets — we cover the crowd-stopping footage.</p>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            [Users, "Team size", "Solo writer for an intimate tasting, full crew for a festival weekend — scaled to what you describe."],
            [Camera, "Photo / video", "Tell us if the experience deserves a photographer, a videographer, or both."],
            [BedDouble, "Overnight stay", "Reviewing a resort or multi-day experience? The host covers the stay for the visiting team."],
            [Music2, "Shows & events", "Artists and promoters cover entry to the performance being reviewed."],
          ].map(([Icon, t, b]) => (
            <div key={t} className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-5" data-testid={`insider-resource-${t.toLowerCase().replace(/\s|\//g, "-")}`}>
              <Icon size={20} className="text-amber-300 mb-2.5" />
              <p className="font-display font-bold text-sm">{t}</p>
              <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="px-6 md:px-12 lg:px-20 py-16 max-w-6xl mx-auto border-t border-white/5">
        <p className="overline mb-3">Simple, four steps</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-10">How the visit works.</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <Step n={1} Icon={Crown} title="Send the invitation" body="Owners click the crown icon on their listing (or dashboard row) and tell us the exact experience to review — a chef's tasting, a VIP cabana day, a sunset cruise — so we can size the team, photographer/videographer and stay." />
          <Step n={2} Icon={ShieldCheck} title="We review & allocate" body="Our editorial desk confirms the fit, assigns the right crew for your experience, and picks a date. You'll get an email the moment your visit is scheduled." />
          <Step n={3} Icon={BadgeCheck} title="We arrive identified" body="No mystery shoppers. Our team shows up with official Gulf Coast VIP credentials so you and your staff know exactly who we are — then we experience it like real guests." />
          <Step n={4} Icon={Sparkles} title="Review goes live" body="Once published, the Insider Review pins above your other reviews with a gold badge and stays there permanently." />
        </div>
      </section>

      {/* Sample reviews */}
      {samples.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-16 max-w-6xl mx-auto border-t border-white/5" data-testid="insider-samples">
          <p className="overline mb-3">Recent Insider Reviews</p>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-10">See what the badge looks like.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {samples.map((r) => <SampleCard key={r.id} r={r} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-4xl mx-auto border-t border-white/5">
        <p className="overline mb-3">Good questions</p>
        <h2 className="font-display font-black text-3xl md:text-4xl mb-8">FAQ.</h2>
        <div className="space-y-5">
          {[
            ["Is there a cost to be reviewed?", "No — Insider Reviews are 100% free and editorial. We never charge to visit or publish. The host covers the experience itself in-kind (the tasting, the tickets, the stay) so our team can genuinely live it — money never changes hands."],
            ["What do I need to provide?", "Just the experience you want reviewed. A restaurant covers the food tasting, an attraction covers the tickets, a resort covers the room, an artist or promoter covers entry to the show. That's what lets us write from a real guest's seat."],
            ["Who shows up — and will I know it's you?", "Yes. Our team arrives identified, carrying official Gulf Coast VIP credentials — never incognito. Team size, photographer or videographer, and stay are allocated based on the experience you describe in your invitation."],
            ["Who writes the review?", "The Gulf Coast VIP editorial desk. Every Insider Review is signed by our team and dated so guests can see it's the real thing."],
            ["Can I choose the visit date?", "You can share preferred nights (best show, signature service, seasonal highlights) and we'll do our best to align."],
            ["What if I don't like the review?", "You can reply publicly right below it. If something factual is off, ping the team from your dashboard and we'll correct it."],
            ["Does every invite get scheduled?", "We prioritize listings that match the season and audience we're covering. If we can't fit you in this round, invite us again next quarter."],
          ].map(([q, a]) => (
            <div key={q} className="rounded-xl border border-white/10 p-5 bg-white/[0.02]">
              <p className="font-display font-bold text-white">{q}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a) }} />
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-4xl mx-auto text-center">
        <div className="rounded-3xl border border-amber-400/40 bg-gradient-to-br from-amber-400/15 via-amber-400/5 to-transparent p-10">
          <Crown size={30} className="mx-auto text-amber-300 mb-4" />
          <h2 className="font-display font-black text-3xl">Ready to earn your Insider Badge?</h2>
          <p className="text-zinc-300 mt-3">Head to your dashboard, click the crown next to any listing, and tell us the experience you'd like reviewed. Free, credentialed, unforgettable.</p>
          <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-bold px-6 py-3 rounded-full" data-testid="insider-final-cta">
            Invite the VIP team <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
